import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import type { components } from '../../../api/schema';
import { useAuth } from '../../../auth/AuthProvider';
import { Badge, Button, Card, Field, Icon, Input, Select } from '../../../design';
import { useToast } from '../../../design/Toast';
import type { LockerDetail } from '../common';
import { LayoutGrid, LayoutLegend } from '../LayoutGrid';
import {
  type Rect,
  type Slot,
  firstFreeCell,
  freeOutputs,
  moveRect,
  nextLabel,
  occupiedCells,
  resizeRect,
} from '../layout';
import s from '../lockers.module.css';

type SlotUpdate = components['schemas']['SlotUpdate'];
type Validation = components['schemas']['LayoutValidation'];

const ERROR_LABEL: Record<Validation['errors'][number]['code'], string> = {
  'control-out-of-grid': 'Ecranul iese din grilă',
  'board-address-duplicate': 'Adresă de placă duplicată',
  'slot-output-invalid': 'Ieșire inexistentă pe placă',
  'slot-output-duplicate': 'Ieșire folosită de două compartimente',
  'slot-out-of-grid': 'Compartiment în afara grilei',
  'slot-overlap': 'Compartimente suprapuse',
  'slot-over-control': 'Compartiment peste ecran',
};

/** Designerul (D4): selectare + inspector, fără drag-and-drop; fiecare modificare trece prin server. */
export function SlotsTab({ locker: l }: { locker: LockerDetail }) {
  const { has } = useAuth();
  const canEdit = has('configure');
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [controlSelected, setControlSelected] = useState(false);
  const selected = l.slots.find((x) => x.id === selectedId) ?? null;

  const validation = useQuery({
    queryKey: ['locker', l.id, 'validate', l.updatedAt, l.slots.length],
    queryFn: () =>
      unwrap(api.POST('/lockers/{id}/layout/validate', { params: { path: { id: l.id } } })),
  });
  const invalidIds = useMemo(() => {
    const set = new Set<string>();
    for (const e of validation.data?.errors ?? []) {
      const m =
        /^slots\[(\d+)\]/.exec(e.path) ??
        /^slots\.(\d+)/.exec(e.path) ??
        /^slot:([0-9a-f-]+)/.exec(e.path);
      if (m) {
        const byIndex = l.slots[Number(m[1])];
        if (byIndex) set.add(byIndex.id);
        else if (l.slots.some((x) => x.id === m[1])) set.add(m[1]!);
      }
      for (const x of l.slots) if (e.path.includes(x.id) || e.path.includes(x.label)) set.add(x.id);
    }
    return set;
  }, [validation.data, l.slots]);
  const controlInvalid = (validation.data?.errors ?? []).some(
    (e) => e.code === 'control-out-of-grid' || e.code === 'slot-over-control',
  );

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['locker', l.id] });
    void qc.invalidateQueries({ queryKey: ['lockers'] });
  };
  const fail = (e: unknown) => toast(errorMessage(e), 'error');

  const patchSlot = useMutation({
    mutationFn: ({ slotId, body }: { slotId: string; body: SlotUpdate }) =>
      unwrap(
        api.PATCH('/lockers/{id}/slots/{slotId}', { params: { path: { id: l.id, slotId } }, body }),
      ),
    onSuccess: refresh,
    onError: fail,
  });
  const addSlot = useMutation({
    mutationFn: (rect?: Rect) => {
      const out = freeOutputs(l.boards, l.slots)[0];
      if (!out)
        throw new Error(
          'Toate ieșirile plăcilor sunt mapate; adaugă o placă sau mărește ieșirile.',
        );
      const cell = rect ?? firstFreeCell(l.grid.cols, occupiedCells(l.slots, l.grid.control));
      if (!cell) throw new Error('Nu există celulă liberă.');
      return unwrap(
        api.POST('/lockers/{id}/slots', {
          params: { path: { id: l.id } },
          body: {
            label: nextLabel(l.slots),
            size: 'M',
            boardId: out.boardId,
            door: out.door,
            rect: cell,
          },
        }),
      );
    },
    onSuccess: (slot) => {
      refresh();
      setSelectedId(slot.id);
      setControlSelected(false);
    },
    onError: fail,
  });
  const removeSlot = useMutation({
    mutationFn: (slotId: string) =>
      unwrap(
        api.DELETE('/lockers/{id}/slots/{slotId}', { params: { path: { id: l.id, slotId } } }),
      ),
    onSuccess: () => {
      refresh();
      setSelectedId(null);
    },
    onError: fail,
  });
  const block = useMutation({
    mutationFn: ({ slotId, blocked }: { slotId: string; blocked: boolean }) =>
      unwrap(
        blocked
          ? api.POST('/lockers/{id}/slots/{slotId}/unblock', {
              params: { path: { id: l.id, slotId } },
            })
          : api.POST('/lockers/{id}/slots/{slotId}/block', {
              params: { path: { id: l.id, slotId } },
            }),
      ),
    onSuccess: refresh,
    onError: fail,
  });
  const gridUpdate = useMutation({
    mutationFn: (body: { cols: number; control: Rect; reflow?: boolean }) =>
      unwrap(api.PUT('/lockers/{id}/layout', { params: { path: { id: l.id } }, body })),
    onSuccess: refresh,
    onError: fail,
  });

  const busy =
    patchSlot.isPending ||
    addSlot.isPending ||
    removeSlot.isPending ||
    block.isPending ||
    gridUpdate.isPending;

  return (
    <div className={s.workspace}>
      <Card>
        <div className="flex between wrap" style={{ marginBottom: 12 }}>
          <div className="flex wrap">
            <h2>Designer</h2>
            {validation.data &&
              (validation.data.valid ? (
                <Badge tone="green">layout valid</Badge>
              ) : (
                <Badge tone="red">
                  {validation.data.errors.length}{' '}
                  {validation.data.errors.length === 1 ? 'eroare' : 'erori'}
                </Badge>
              ))}
            {validation.data && (
              <span className="small muted">
                {validation.data.stats.slots} compartimente · {validation.data.stats.outputs} ieșiri
                · {validation.data.stats.unmappedOutputs} nemapate
              </span>
            )}
          </div>
          {canEdit && (
            <div className="flex">
              <Button
                small
                icon="plus"
                onClick={() => addSlot.mutate(undefined)}
                busy={addSlot.isPending}
                disabled={busy}
              >
                Adaugă compartiment
              </Button>
              <Button
                small
                onClick={() => {
                  setControlSelected(true);
                  setSelectedId(null);
                }}
              >
                Grilă & ecran
              </Button>
            </div>
          )}
        </div>
        <div className={s.gridWrap}>
          <LayoutGrid
            cols={l.grid.cols}
            control={l.grid.control}
            slots={l.slots}
            boards={l.boards}
            selectedId={selectedId}
            invalidSlotIds={invalidIds}
            controlInvalid={controlInvalid}
            onSelectSlot={(sl) => {
              setSelectedId(sl.id);
              setControlSelected(false);
            }}
            onSelectControl={() => {
              setControlSelected(true);
              setSelectedId(null);
            }}
            onSelectEmpty={canEdit ? (x, y) => addSlot.mutate({ x, y, w: 1, h: 1 }) : undefined}
          />
          <LayoutLegend />
        </div>
        {validation.data && !validation.data.valid && (
          <ul className="small" style={{ color: 'var(--red)', marginTop: 12, paddingLeft: 18 }}>
            {validation.data.errors.map((e, i) => (
              <li key={i}>
                <strong>{ERROR_LABEL[e.code] ?? e.code}</strong> — {e.message}{' '}
                <span className="muted mono">({e.path})</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <aside className={s.inspector} aria-label="Inspector">
        {controlSelected ? (
          <GridInspector
            key={`${l.grid.cols}:${JSON.stringify(l.grid.control)}`}
            cols={l.grid.cols}
            control={l.grid.control}
            canEdit={canEdit}
            busy={gridUpdate.isPending}
            onSave={(b) => gridUpdate.mutate(b)}
          />
        ) : selected ? (
          <SlotInspector
            key={`${selected.id}:${l.configVersion}`}
            slot={selected}
            locker={l}
            canEdit={canEdit}
            busy={busy}
            onPatch={(body) => patchSlot.mutate({ slotId: selected.id, body })}
            onBlock={() =>
              block.mutate({ slotId: selected.id, blocked: selected.techState === 'blocked' })
            }
            onRemove={() => removeSlot.mutate(selected.id)}
          />
        ) : (
          <div className="muted small">
            <Icon name="info" size={18} />
            <p style={{ marginTop: 8 }}>
              Alege un compartiment din grilă pentru a-l edita, apasă o celulă liberă pentru a
              adăuga unul, sau „Grilă & ecran” pentru coloane și poziția ecranului.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}

function SlotInspector({
  slot,
  locker: l,
  canEdit,
  busy,
  onPatch,
  onBlock,
  onRemove,
}: {
  slot: Slot;
  locker: LockerDetail;
  canEdit: boolean;
  busy: boolean;
  onPatch: (b: SlotUpdate) => void;
  onBlock: () => void;
  onRemove: () => void;
}) {
  const [label, setLabel] = useState(slot.label);
  const [size, setSize] = useState(slot.size);
  const [output, setOutput] = useState(`${slot.boardId}:${slot.door}`);
  const [dims, setDims] = useState(slot.dimensionsMm);
  const [sensor, setSensor] = useState(slot.sensor);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const outputs = useMemo(() => {
    const seen = new Set<string>();
    return [
      { boardId: slot.boardId, door: slot.door },
      ...freeOutputs(l.boards, l.slots, slot.id),
    ].filter((o) => {
      const k = `${o.boardId}:${o.door}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [l.boards, l.slots, slot]);
  const boardName = (id: string) => l.boards.find((b) => b.id === id)?.name ?? '?';
  const dirty =
    label !== slot.label ||
    size !== slot.size ||
    output !== `${slot.boardId}:${slot.door}` ||
    JSON.stringify(dims) !== JSON.stringify(slot.dimensionsMm) ||
    sensor !== slot.sensor;
  const save = () => {
    const [boardId, door] = output.split(':');
    onPatch({
      label,
      size,
      boardId,
      door: Number(door),
      dimensionsMm: {
        width: Number(dims.width),
        height: Number(dims.height),
        depth: Number(dims.depth),
      },
      sensor,
    });
  };
  const move = (dx: number, dy: number) =>
    onPatch({ rect: moveRect(slot.rect, dx, dy, l.grid.cols) });
  const resize = (dw: number, dh: number) =>
    onPatch({ rect: resizeRect(slot.rect, dw, dh, l.grid.cols) });
  const blocked = slot.techState === 'blocked';
  return (
    <>
      <div className="flex between">
        <h3>Compartiment {slot.label}</h3>
        <Badge
          tone={
            blocked
              ? 'neutral'
              : slot.occupancy === 'occupied'
                ? 'blue'
                : slot.occupancy === 'reserved'
                  ? 'orange'
                  : 'green'
          }
        >
          {blocked
            ? 'blocat tehnic'
            : slot.occupancy === 'occupied'
              ? 'ocupat'
              : slot.occupancy === 'reserved'
                ? 'rezervat'
                : 'liber'}
        </Badge>
      </div>
      <div className="small muted">
        ușă {slot.doorOpen ? <span style={{ color: 'var(--red)' }}>deschisă</span> : 'închisă'} ·
        poziție {slot.rect.x + 1},{slot.rect.y + 1} · {slot.rect.w}×{slot.rect.h}
      </div>
      <div className={s.formGrid}>
        <Field label="Etichetă" htmlFor="si-label">
          <Input
            id="si-label"
            value={label}
            maxLength={8}
            disabled={!canEdit}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
        <Field label="Dimensiune" htmlFor="si-size">
          <Select
            id="si-size"
            value={size}
            disabled={!canEdit}
            onChange={(e) => setSize(e.target.value as Slot['size'])}
          >
            {['S', 'M', 'L', 'XL'].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Placă și ieșire" htmlFor="si-out">
        <Select
          id="si-out"
          value={output}
          disabled={!canEdit}
          onChange={(e) => setOutput(e.target.value)}
        >
          {outputs.map((o) => (
            <option key={`${o.boardId}:${o.door}`} value={`${o.boardId}:${o.door}`}>
              {boardName(o.boardId)} · ieșirea {o.door}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Poziție și mărime (celule)">
        <div className="flex wrap">
          <Button
            small
            aria-label="Mută la stânga"
            disabled={!canEdit || busy}
            onClick={() => move(-1, 0)}
          >
            ←
          </Button>
          <Button
            small
            aria-label="Mută la dreapta"
            disabled={!canEdit || busy}
            onClick={() => move(1, 0)}
          >
            →
          </Button>
          <Button
            small
            aria-label="Mută în sus"
            disabled={!canEdit || busy}
            onClick={() => move(0, -1)}
          >
            ↑
          </Button>
          <Button
            small
            aria-label="Mută în jos"
            disabled={!canEdit || busy}
            onClick={() => move(0, 1)}
          >
            ↓
          </Button>
          <Button
            small
            aria-label="Mai lat"
            disabled={!canEdit || busy}
            onClick={() => resize(1, 0)}
          >
            +lat
          </Button>
          <Button
            small
            aria-label="Mai îngust"
            disabled={!canEdit || busy || slot.rect.w <= 1}
            onClick={() => resize(-1, 0)}
          >
            −lat
          </Button>
          <Button
            small
            aria-label="Mai înalt"
            disabled={!canEdit || busy}
            onClick={() => resize(0, 1)}
          >
            +înalt
          </Button>
          <Button
            small
            aria-label="Mai scund"
            disabled={!canEdit || busy || slot.rect.h <= 1}
            onClick={() => resize(0, -1)}
          >
            −înalt
          </Button>
        </div>
      </Field>
      <Field label="Dimensiuni (mm) L × Î × A">
        <div className="flex">
          <Input
            aria-label="Lățime mm"
            type="number"
            value={dims.width}
            disabled={!canEdit}
            onChange={(e) => setDims({ ...dims, width: Number(e.target.value) })}
          />
          <Input
            aria-label="Înălțime mm"
            type="number"
            value={dims.height}
            disabled={!canEdit}
            onChange={(e) => setDims({ ...dims, height: Number(e.target.value) })}
          />
          <Input
            aria-label="Adâncime mm"
            type="number"
            value={dims.depth}
            disabled={!canEdit}
            onChange={(e) => setDims({ ...dims, depth: Number(e.target.value) })}
          />
        </div>
      </Field>
      <Field label="Senzor de ușă" htmlFor="si-sensor">
        <Select
          id="si-sensor"
          value={sensor}
          disabled={!canEdit}
          onChange={(e) => setSensor(e.target.value as Slot['sensor'])}
        >
          <option value="NC">Normal închis (NC)</option>
          <option value="NO">Normal deschis (NO)</option>
          <option value="none">Fără senzor</option>
        </Select>
      </Field>
      {canEdit && (
        <>
          <Button variant="primary" onClick={save} disabled={!dirty || busy}>
            Salvează
          </Button>
          <div className="sep" style={{ margin: '6px 0' }} />
          <Button
            icon={blocked ? 'refresh' : 'lock'}
            onClick={onBlock}
            disabled={busy || (!blocked && (slot.occupancy !== 'free' || slot.doorOpen))}
            title={
              !blocked && slot.occupancy !== 'free'
                ? 'Doar compartimentele libere se pot bloca'
                : undefined
            }
          >
            {blocked ? 'Deblochează' : 'Blocare tehnică'}
          </Button>
          {confirmRemove ? (
            <Button variant="danger" onClick={onRemove} busy={busy}>
              Confirmă eliminarea
            </Button>
          ) : (
            <Button
              variant="danger"
              icon="trash"
              onClick={() => setConfirmRemove(true)}
              disabled={busy || slot.occupancy !== 'free' || slot.doorOpen}
            >
              Elimină compartimentul
            </Button>
          )}
        </>
      )}
    </>
  );
}

function GridInspector({
  cols,
  control,
  canEdit,
  busy,
  onSave,
}: {
  cols: number;
  control: Rect;
  canEdit: boolean;
  busy: boolean;
  onSave: (b: { cols: number; control: Rect; reflow?: boolean }) => void;
}) {
  const [c, setC] = useState(cols);
  const [ctrl, setCtrl] = useState(control);
  const [reflow, setReflow] = useState(false);
  return (
    <>
      <h3>Grilă și ecran</h3>
      <Field label="Coloane (3–8)" htmlFor="gi-cols">
        <Input
          id="gi-cols"
          type="number"
          min={3}
          max={8}
          value={c}
          disabled={!canEdit}
          onChange={(e) => setC(Number(e.target.value))}
        />
      </Field>
      <Field label="Ecranul (coloană, rând, lățime, înălțime)">
        <div className="flex">
          <Input
            aria-label="Ecran coloană"
            type="number"
            min={1}
            max={c}
            value={ctrl.x + 1}
            disabled={!canEdit}
            onChange={(e) => setCtrl({ ...ctrl, x: Number(e.target.value) - 1 })}
          />
          <Input
            aria-label="Ecran rând"
            type="number"
            min={1}
            value={ctrl.y + 1}
            disabled={!canEdit}
            onChange={(e) => setCtrl({ ...ctrl, y: Number(e.target.value) - 1 })}
          />
          <Input
            aria-label="Ecran lățime"
            type="number"
            min={1}
            max={8}
            value={ctrl.w}
            disabled={!canEdit}
            onChange={(e) => setCtrl({ ...ctrl, w: Number(e.target.value) })}
          />
          <Input
            aria-label="Ecran înălțime"
            type="number"
            min={1}
            max={8}
            value={ctrl.h}
            disabled={!canEdit}
            onChange={(e) => setCtrl({ ...ctrl, h: Number(e.target.value) })}
          />
        </div>
      </Field>
      <label className="flex small">
        <input
          type="checkbox"
          checked={reflow}
          disabled={!canEdit}
          onChange={(e) => setReflow(e.target.checked)}
        />
        Re-împachetează compartimentele după schimbare
      </label>
      {canEdit && (
        <Button
          variant="primary"
          onClick={() => onSave({ cols: c, control: ctrl, reflow })}
          busy={busy}
        >
          Aplică
        </Button>
      )}
    </>
  );
}
